import React, { useState } from 'react';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAsyncMemo } from 'services/TypeUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import Icon from 'components/Utils/NewIcon';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import UploadFile from 'components/Utils/UploadFile';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';

type Props = {
  onCopy?: (text: string) => string;
};

function AssetsManager({ onCopy }: Props) {
  const dispatch = useAppDispatch();
  const [openImage, setOpenImage] = useState<string | undefined>();
  const [imageToDelete, setImageToDelete] = useState<string | undefined>();
  const isDeleteingImage = useIsLoading(api.Resource.deletePublicResource);
  const [reload, setReload] = useState(0);

  const urls = useAsyncMemo(() => {
    return dispatch(api.Resource.listPublicResources());
  }, [reload]);

  function copyToClipboard(url: string) {
    let text = url;
    if (onCopy) {
      text = onCopy(text);
    }
    window.navigator.clipboard.writeText(text).then(() => {
      MiscUtils.showToastrInfo({ title: 'url copied to clipboard' });
    });
  }

  async function deleteImage(url: string) {
    await dispatch(api.Resource.deletePublicResource({ url }));
    setImageToDelete(undefined);
    setReload((prev) => prev + 1);
  }

  async function uploadFile(file: Blob) {
    await dispatch(api.Resource.uploadPublicResource({ file }), {
      errorTitle: 'Failed to upload image',
    });
    setReload((prev) => prev + 1);
  }

  return (
    <div className="assets-manager">
      <ConfirmationDialog
        open={!!imageToDelete}
        title="Please Confirm"
        message="Are you sure you want to delete this image?"
        loadingState={isDeleteingImage}
        onConfirm={() => deleteImage(imageToDelete!)}
        onCancel={() => setImageToDelete(undefined)}
      />
      <div className="thumbnails">
        <UploadFile title="" accept="image/jpeg, image/png, image/webp" onSubmit={uploadFile} />
        {urls?.values?.map((url) => {
          const isOpen = openImage === url;
          return (
            <div className={`thumbnail  ${isOpen ? 'is-open' : ''}`} key={url}>
              <img src={url} />
              <div className="buttons">
                <DefaultButton
                  name={isOpen ? 'Close' : 'Open'}
                  icon="eye"
                  onClick={() => {
                    setOpenImage((prev) => (prev === url ? undefined : url));
                    setTimeout(() => {
                      document.querySelector(`img[src="${url}"]`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }, 400);
                  }}
                />
                <DefaultButton
                  name="Copy Url"
                  icon="clipboard"
                  onClick={() => copyToClipboard(url)}
                />
                <DefaultButton
                  name="Remove"
                  icon="trash"
                  className="red"
                  onClick={() => setImageToDelete(url)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AssetsManager;
